import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getImagingOrder, updateImagingStatus, postImagingReport } from '../../api/orders.js'
import { listDocuments, uploadDocument } from '../../api/documents.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import DocumentLink from '../../components/DocumentLink.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import CrossFacilityErrorCard from '../../components/CrossFacilityErrorCard.jsx'
import { formatDateTime, statusColor, dobAge } from '../../utils/format.js'
import { IMAGING_TRANSITIONS, ORDER_STATUS } from '../../constants/index.js'

/**
 * Imaging study detail for radiology staff. Matches the lab detail page
 * in spirit: list the ordered studies, allow status transitions, and
 * capture a short report summary once the study is complete.
 */
export default function RadiologyOrderDetail() {
  const { orderId } = useParams()
  const qc = useQueryClient()
  const [comment, setComment] = useState('')                  // status-change comment
  const [summary, setSummary] = useState('')                  // report_summary (final)
  // NEW imaging-side fields driving the doctor's lifecycle UX:
  const [resultStatus, setResultStatus] = useState('pending')
  const [prelim, setPrelim] = useState('')
  const [impression, setImpression] = useState('')
  const [radComment, setRadComment] = useState('')

  const q = useQuery({
    queryKey: ['imaging-order', orderId],
    queryFn: () => getImagingOrder(orderId),
    enabled: Boolean(orderId),
  })
  const order = q.data

  // Track what was last successfully saved so the button can:
  //   * disable when there's nothing new to save (typed text === last saved)
  //   * flash "Saved ✓" briefly after success so the tech sees the loop closed
  // Without this the button just toggles "Saving…" → "Save report" with no
  // confirmation, leaving the tech wondering whether the click did anything.
  // Declared BEFORE the useEffect that uses them.
  const [lastSavedReport, setLastSavedReport] = useState('')
  const [reportSavedFlash, setReportSavedFlash] = useState(false)

  useEffect(() => {
    if (order?.report_summary && !summary) setSummary(order.report_summary)
    if (order?.preliminary_imaging_summary && !prelim) setPrelim(order.preliminary_imaging_summary)
    if (order?.impression && !impression) setImpression(order.impression)
    if (order?.result_comment && !radComment) setRadComment(order.result_comment)
    if (order?.result_status) setResultStatus(order.result_status)
    if (order?.report_summary != null) {
      setLastSavedReport((order.report_summary || '').trim())
    }
  }, [order]) // eslint-disable-line react-hooks/exhaustive-deps

  const statusMut = useMutation({
    mutationFn: ({ next, comment }) => updateImagingStatus(orderId, next, comment),
    onSuccess: (fresh) => {
      qc.setQueryData(['imaging-order', orderId], fresh)
      qc.invalidateQueries({ queryKey: ['radiology-queue'] })
      setComment('')
    },
  })
  const reportMut = useMutation({
    mutationFn: (body) => postImagingReport(orderId, body),
    onSuccess: (fresh) => {
      qc.setQueryData(['imaging-order', orderId], fresh)
      setLastSavedReport((fresh?.report_summary || '').trim())
      setReportSavedFlash(true)
      setTimeout(() => setReportSavedFlash(false), 2500)
    },
  })

  // Attached result files for this imaging order (polymorphic
  // ClinicalDocument). Lets the radiologist attach the PACS print,
  // scanned film, DICOM-derived JPEG, or a free-form report document —
  // visible to the ordering doctor and the patient via their documents
  // views.
  const docsQ = useQuery({
    queryKey: ['imaging-order-docs', orderId],
    queryFn: () =>
      listDocuments({
        owner_model: 'clinical_notes.ImagingOrder',
        owner_id: orderId,
      }),
    enabled: Boolean(orderId),
  })

  // ---- result-file upload ---------------------------------------------------
  const [file, setFile] = useState(null)
  const [fileDesc, setFileDesc] = useState('')
  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a result file first.')
      return uploadDocument({
        owner_model: 'clinical_notes.ImagingOrder',
        owner_id: orderId,
        file,
        // 'imaging_report' is the canonical document.kind for an
        // uploaded imaging file (PACS print, scanned film, DICOM-derived
        // JPEG). Distinct from 'radiology_report' which is the BRANDED
        // PDF generated from the typed report_summary fields above.
        kind: 'imaging_report',
        description: fileDesc,
      })
    },
    onSuccess: () => {
      setFile(null)
      setFileDesc('')
      qc.invalidateQueries({ queryKey: ['imaging-order-docs', orderId] })
    },
  })

  // Tab state must be declared BEFORE any early return so the hook
  // count is constant across renders (Rules of Hooks). Default tab is
  // Report — the radiologist's main work surface.
  const [tab, setTab] = useState('report')

  if (q.isLoading) return <div className="p-4 text-sm text-slate-400">Loading…</div>
  if (q.isError || !order) {
    // Cross-centre 403 → CrossFacilityErrorCard renders the tel: call
    // screen so the radiology user can phone the correct centre.
    const env = q.error?.response?.data?.error
    if (env?.code === 'cross_centre_access') {
      return (
        <div className="space-y-3">
          <CrossFacilityErrorCard
            error={q.error}
            backTo="/radiology"
            backLabel="Back to queue"
          />
        </div>
      )
    }
    return (
      <div className="space-y-3">
        <ErrorBanner error={q.error || new Error('Order not found')} onRetry={() => q.refetch()} />
        <Link to="/radiology" className="text-xs underline">Back to queue</Link>
      </div>
    )
  }

  const nexts = IMAGING_TRANSITIONS[(order.status || '').toLowerCase()] || []
  const canEnterReport = [
    ORDER_STATUS.IN_PROGRESS,
    ORDER_STATUS.ACCEPTED,
    ORDER_STATUS.COMPLETED,
  ].includes((order.status || '').toLowerCase())
  const items = order.items && order.items.length
    ? order.items
    : (order.imaging_type ? [{ id: 'legacy', study_name: order.imaging_type }] : [])

  // Patient-banner identity (name, sex, DOB + age, phone) is confirmed at a
  // glance before any study — imaging is high-risk (radiation, contrast,
  // pregnancy). DOB + age formatting is shared via dobAge() so it reads the
  // same here, on every other screen, and on the printed documents.

  // Tab state for the workflow sections — keeps the page calm and
  // organised instead of a long scroll of stacked cards. The patient
  // banner + order metadata + studies list always render above the
  // tabs as the "context" panel (mirrors how the org dashboard pins
  // its brand header above its tabs). Default tab is Report because
  // that's the radiologist's main work surface.
  const TABS = [
    { id: 'report',   label: 'Report' },
    { id: 'files',    label: 'Files' },
    { id: 'workflow', label: 'Workflow', hidden: nexts.length === 0 },
  ].filter((t) => !t.hidden)
  // (setTab/tab declared higher up — hook must precede early returns.)

  return (
    <div className="space-y-4">
      {/* Patient banner — prominent so the radiologist confirms the recipient
          before any imaging is performed. */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Patient</div>
            <div className="text-xl font-semibold text-slate-800 break-words">
              {order.patient_name || 'Unknown patient'}
            </div>
            <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
              {order.patient_sex && <span className="capitalize">{order.patient_sex}</span>}
              {order.patient_dob && <span>{dobAge(order.patient_dob)}</span>}
              {order.patient_phone && <span>☎ {order.patient_phone}</span>}
            </div>
          </div>
          <Link to="/radiology" className="text-xs underline">Back</Link>
        </div>
      </Card>

      <Card>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Imaging order</div>
          <div className="font-semibold">#{order.id}</div>
          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-0.5">
            <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(order.status)}`}>{order.status}</span>
            <span>Requested {formatDateTime(order.created_at)}</span>
            {order.radiology_center_name && <span>· {order.radiology_center_name}</span>}
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title="Studies" />
        <ul className="divide-y divide-slate-100">
          {items.length === 0 && <li className="py-3 text-sm text-slate-400">No studies listed.</li>}
          {items.map((it) => (
            <li key={it.id || it.study_code} className="py-2 flex items-center justify-between">
              <div>
                <div className="text-sm">{it.study_name}</div>
                <div className="text-[11px] text-slate-500">
                  {it.study_code && <span className="font-mono mr-2">{it.study_code}</span>}
                  {it.modality && <span>{it.modality}</span>}
                </div>
              </div>
              {it.urgency && (
                <span className="text-[10px] uppercase border border-slate-200 rounded-full px-2 py-0.5 text-slate-600">{it.urgency}</span>
              )}
            </li>
          ))}
        </ul>
        {order.clinical_indication && (
          <div className="mt-3 text-xs text-slate-500">
            <div className="uppercase tracking-wide text-[10px] text-slate-400">Clinical indication</div>
            <div className="mt-1 whitespace-pre-wrap">{order.clinical_indication}</div>
          </div>
        )}
      </Card>

      {/* ─── Tab strip — Report / Files / Workflow ──────────────────── */}
      <div className="border-b-2 border-slate-200 flex flex-wrap gap-2">
        {TABS.map((t) => {
          const active = tab === t.id
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={
                `px-6 py-3 text-base tracking-wide border-b-[3px] -mb-[2px] transition-colors ` +
                (active
                  ? 'border-wantok-700 text-wantok-800 font-bold bg-wantok-50/60'
                  : 'border-transparent text-slate-600 font-semibold hover:text-wantok-800 hover:bg-slate-50')
              }
            >
              {t.label}
            </button>
          )
        })}
      </div>

      {tab === 'workflow' && nexts.length > 0 && (
        <Card>
          <CardHeader title="Workflow actions" subtitle="Move the order through its admin lifecycle. The ordering clinician sees a colour-coded badge driven by these transitions." />
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional comment"
            className="w-full border rounded px-2 py-1.5 text-sm"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {nexts.map(({ label, next }) => (
              <button
                key={next}
                onClick={() => statusMut.mutate({ next, comment })}
                disabled={statusMut.isPending}
                className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
              >
                {label}
              </button>
            ))}
          </div>
          {statusMut.isError && <div className="mt-2"><ErrorBanner error={statusMut.error} /></div>}
        </Card>
      )}

      {tab === 'report' && canEnterReport && (
        <Card>
          <CardHeader
            title="Report"
            subtitle="Set the lifecycle status — the doctor sees a colour-coded badge driven by this. Preliminary surfaces immediately for empirical-treatment decisions; the impression is your bottom line."
          />

          {/* Status selector */}
          <div className="mb-3">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">Status</label>
            <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm">
              {[
                { v: 'pending',     label: 'Pending',     tone: 'bg-slate-100 text-slate-700' },
                { v: 'preliminary', label: 'Preliminary', tone: 'bg-sky-100 text-sky-800' },
                { v: 'final',       label: 'Final',       tone: 'bg-emerald-100 text-emerald-800' },
              ].map((s) => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => setResultStatus(s.v)}
                  className={
                    'px-4 py-2 font-semibold border-r border-slate-300 last:border-r-0 transition ' +
                    (resultStatus === s.v ? s.tone : 'bg-white text-slate-500 hover:bg-slate-50')
                  }
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preliminary summary — early read */}
          <div className="mb-3">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">Preliminary imaging summary</label>
            <textarea
              rows={3} value={prelim} onChange={(e) => setPrelim(e.target.value)}
              placeholder="e.g. Right basal opacity, likely consolidation. Awaiting comparison films."
              className="w-full border border-slate-300 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Impression — radiologist's bottom line */}
          <div className="mb-3">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">Impression</label>
            <textarea
              rows={2} value={impression} onChange={(e) => setImpression(e.target.value)}
              placeholder="e.g. Right lower lobe consolidation consistent with pneumonia."
              className="w-full border border-slate-300 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Final report summary */}
          <div className="mb-3">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">Final report summary</label>
            <textarea
              rows={4} value={summary} onChange={(e) => setSummary(e.target.value)}
              placeholder="e.g. Frontal CXR shows right lower lobe airspace opacity with air bronchograms…"
              className="w-full border border-slate-300 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Comment */}
          <div className="mb-3">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">Radiologist comments (optional)</label>
            <textarea
              rows={2} value={radComment} onChange={(e) => setRadComment(e.target.value)}
              placeholder="e.g. Suboptimal inspiration, consider repeat if clinical concern persists."
              className="w-full border border-slate-300 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="mt-2 flex items-center justify-end gap-3">
            {reportMut.isPending && (
              <span className="text-xs text-slate-500">Saving…</span>
            )}
            {!reportMut.isPending && reportSavedFlash && (
              <span className="text-xs font-semibold text-emerald-700">Saved ✓</span>
            )}
            <button
              onClick={() => reportMut.mutate({
                summary, prelim, impression, comment: radComment, result_status: resultStatus,
              })}
              disabled={reportMut.isPending}
              className="text-sm font-semibold bg-wantok-700 hover:bg-wantok-800 disabled:bg-slate-300 text-white rounded-lg px-4 py-2 transition"
            >
              {reportMut.isPending ? 'Saving…' : 'Save report'}
            </button>
          </div>
          {reportMut.isError && <div className="mt-2"><ErrorBanner error={reportMut.error} /></div>}
        </Card>
      )}

      {/*
        Result files. Lets the radiologist attach the PACS print, scanned
        film, DICOM-derived JPEG, or a typed report document. Bound to
        the ImagingOrder polymorphically so the ordering doctor and the
        patient can open these from their own documents view, alongside
        the typed preliminary/final report fields above.
      */}
      {tab === 'files' && canEnterReport && (
        <Card>
          <CardHeader
            title="Result files"
            subtitle="Attach the PACS print, scanned film, or report (PDF/image/CSV). The doctor and patient will be able to open these."
          />
          {docsQ.error && <ErrorBanner error={docsQ.error} onRetry={() => docsQ.refetch()} />}
          <ul className="divide-y divide-slate-100">
            {docsQ.isLoading && (
              <li className="py-3 text-sm text-slate-400">Loading attachments…</li>
            )}
            {!docsQ.isLoading && (docsQ.data || []).length === 0 && (
              <li className="py-3 text-sm text-slate-400">No files uploaded yet.</li>
            )}
            {(docsQ.data || []).map((d) => (
              <li key={d.id} className="py-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {d.original_filename || `Document #${d.id}`}
                  </div>
                  <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                    <span className="uppercase border rounded-full px-2 py-0.5 text-[10px] bg-slate-50 border-slate-200 text-slate-600">
                      {d.kind || 'imaging_report'}
                    </span>
                    {d.size_bytes != null && <span>{Math.round(d.size_bytes / 1024)} KB</span>}
                    {d.mime_type && <span>{d.mime_type}</span>}
                    <span>Uploaded {formatDateTime(d.uploaded_at)}</span>
                    {d.uploaded_by_username && <span>· by {d.uploaded_by_username}</span>}
                  </div>
                  {d.description && (
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{d.description}</div>
                  )}
                </div>
                <DocumentLink doc={d} />
              </li>
            ))}
          </ul>

          {/* Upload row sits flush below the file list — the card title
              "Result files" already establishes the context, so a second
              "Upload a result file" sub-heading would just be noise. */}
          <div className="border-t pt-3 mt-3 space-y-2">
            <div className="grid gap-2 md:grid-cols-[1fr_14rem_6rem] items-center">
              <input
                type="file"
                className="text-sm"
                // PDF + common image formats + DICOM-friendly extensions
                // (TIFF, BMP, .dcm) so radiologists can drop the PACS export
                // straight in. CSV/XLSX kept for measurement summaries.
                accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.bmp,.dcm,.csv,.xlsx,.xls,.txt"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <input
                type="text"
                placeholder="Description (e.g. PA + lateral CXR report)"
                value={fileDesc}
                onChange={(e) => setFileDesc(e.target.value)}
                className="px-2 py-1 border rounded text-sm"
              />
              <button
                onClick={() => uploadMut.mutate()}
                disabled={!file || uploadMut.isPending}
                className="px-3 py-1 bg-wantok-700 text-white text-sm rounded disabled:bg-slate-400"
              >
                {uploadMut.isPending ? 'Uploading…' : 'Upload'}
              </button>
            </div>
            {uploadMut.isError && (
              <ErrorBanner error={uploadMut.error} />
            )}
            <div className="text-[11px] text-slate-400">
              Max 25 MB per file. Files attach to this imaging order and become
              visible to the ordering doctor and the patient.
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
